import { motion } from 'framer-motion'
import { useEffect } from 'react'
import { Icon } from '../components/Icon'
import { Logo } from '../components/Logo'
import { cx } from '../components/ui'
import {
  EFFECTIVE_DATE,
  LEGAL_DOCUMENTS,
  OPERATOR,
  type LegalDocument,
} from '../legal/documents'

const EASE = [0.22, 1, 0.36, 1] as const

/**
 * The published policies.
 *
 * Rendered outside the signed-in app, because the people most likely to read
 * these — a payment reviewer, someone deciding whether to hand over money — do
 * not have an account and should not need one.
 */
export function LegalScreen({ document }: { document: LegalDocument }) {
  // The tab title is what a reviewer sees in their history; a page of terms
  // labelled "ClickLinx — reflex duels" is not obviously the terms.
  useEffect(() => {
    const previous = window.document.title
    window.document.title = `${document.title} — ClickLinx`
    return () => {
      window.document.title = previous
    }
  }, [document.title])

  return (
    <div className="min-h-dvh pad-safe pad-safe-top">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between gap-4 py-4">
        <a href="/" className="transition-opacity hover:opacity-80">
          <Logo size="sm" />
        </a>
        <a
          href="/"
          className="flex h-11 items-center gap-2 rounded-xl pr-1 text-sm text-fg-muted transition-colors hover:text-fg"
        >
          <Icon name="arrowLeft" size={16} />
          Back to the game
        </a>
      </header>

      <main className="mx-auto w-full max-w-3xl pb-20">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
        >
          <p className="eyebrow mb-3">Policies</p>
          <h1 className="display-title text-3xl text-fg sm:text-4xl">{document.title}</h1>
          <p className="mt-3 max-w-prose text-sm leading-relaxed text-fg-muted">
            {document.summary}
          </p>
          <p className="mt-4 font-mono text-xs tnum text-fg-dim">
            In effect from {EFFECTIVE_DATE}
          </p>
        </motion.div>

        <nav aria-label="Policies" className="mt-8 flex flex-wrap gap-2">
          {LEGAL_DOCUMENTS.map((entry) => (
            <a
              key={entry.slug}
              href={entry.path}
              aria-current={entry.slug === document.slug ? 'page' : undefined}
              className={cx(
                'rounded-xl border px-3.5 py-2 text-sm transition-colors',
                entry.slug === document.slug
                  ? 'border-lime/40 bg-lime/10 text-lime'
                  : 'border-ink-700 text-fg-muted hover:border-ink-600 hover:text-fg',
              )}
            >
              {entry.title}
            </a>
          ))}
        </nav>

        <div className="hairline my-8" />

        <article className="space-y-9">
          {document.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="display-title text-base text-fg">{section.heading}</h2>
              {section.paragraphs?.map((text) => (
                <p key={text} className="mt-3 max-w-prose text-sm leading-relaxed text-fg-muted">
                  {text}
                </p>
              ))}
              {section.list && (
                <ul className="mt-3 max-w-prose space-y-2.5">
                  {section.list.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-relaxed text-fg-muted">
                      <span className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-lime" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </article>

        <div className="hairline my-8" />

        <footer className="text-xs leading-relaxed text-fg-dim">
          <p>
            {OPERATOR.legalName}, {OPERATOR.address}. Questions about these policies:{' '}
            <a href={`mailto:${OPERATOR.support}`} className="text-fg-muted hover:text-fg">
              {OPERATOR.support}
            </a>
            .
          </p>
          <p className="mt-2">
            Practice coins are free, hold no cash value, and cannot be withdrawn. Prize contests
            are contests of skill and are not offered everywhere.
          </p>
        </footer>
      </main>
    </div>
  )
}

/** The row of policy links shown at the bottom of the app. */
export function LegalLinks({ className }: { className?: string }) {
  return (
    // The links stay small; the things you tap do not. Each one carries a
    // 44px-tall hit area, which on a phone is the difference between reading
    // the refund policy and mashing at a 16px line of text.
    <nav
      aria-label="Policies"
      className={cx('-my-2.5 flex flex-wrap items-center gap-x-4', className)}
    >
      {LEGAL_DOCUMENTS.map((entry) => (
        <a
          key={entry.slug}
          href={entry.path}
          className="inline-flex min-h-11 items-center text-xs text-fg-dim transition-colors hover:text-fg-muted"
        >
          {entry.title}
        </a>
      ))}
    </nav>
  )
}
